import React, { useState, useEffect, useCallback } from 'react';
import { 
  TextField, 
  Grid, 
  Typography, 
  Box, 
  Button, 
  Alert, 
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormHelperText
} from '@mui/material';
import { SignupData } from 'form-application-shared';
import { formApi } from '../services/api'; // Adjust the import path as needed

interface PaymentStepProps {
  formData: SignupData;
  setFormData: React.Dispatch<React.SetStateAction<SignupData>>;
  onValidationChange: (validationFn: () => boolean) => void;
  formId: string | null; // Add formId prop, making it optional for backward compatibility
}

interface PaymentErrors {
  optIn?: string;
  acknowledgedTerms?: string;
  entityName?: string;
  bankName?: string;
  routingNumber?: string;
  accountNumber?: string;
  accountNumberConfirm?: string;
  voidedCheckFile?: string;
}

const PaymentStep: React.FC<PaymentStepProps> = ({ formData, setFormData, onValidationChange, formId }) => {
  const [accountNumberConfirm, setAccountNumberConfirm] = useState<string>("");
  const [errors, setErrors] = useState<PaymentErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [uploadingCheck, setUploadingCheck] = useState<boolean>(false);

  // Set account number confirmation from form data on initial load
  useEffect(() => {
    if (formData?.ach?.accountNumberConfirm) {
      setAccountNumberConfirm(formData.ach.accountNumberConfirm);
    }
  }, [formData?.ach?.accountNumberConfirm]);

  // Validation function
  const validateStep = useCallback(() => {
    // If reinsurance is not enabled, no validation needed
    if (!formData.hasReinsurance) {
      return true;
    }
    
    if (!formData || !formData.ach) return false;
    
    let isValid = true;
    const newErrors: PaymentErrors = {};

    // First check if opt-in decision has been made
    if (formData.ach.optIn === null || formData.ach.optIn === undefined) {
      newErrors.optIn = 'Please select whether you want to opt in or opt out of ACH payments';
      isValid = false;
    }

    // If opted in, validate all ACH fields
    if (formData.ach.optIn === true) {
      // Check acknowledgment
      if (!formData.ach.acknowledgedTerms) {
        newErrors.acknowledgedTerms = 'You must acknowledge the one-time formation fee to continue';
        isValid = false;
      }

      // Validate entity name
      if (!formData.ach.entityName) {
        newErrors.entityName = 'Entity name is required';
        isValid = false;
      }

      // Validate bank name
      if (!formData.ach.bankName) {
        newErrors.bankName = 'Bank name is required';
        isValid = false;
      }

      // Validate routing number
      if (!formData.ach.routingNumber) {
        newErrors.routingNumber = 'Routing number is required';
        isValid = false;
      } else if (!/^\d{9}$/.test(formData.ach.routingNumber)) {
        newErrors.routingNumber = 'Routing number must be 9 digits';
        isValid = false;
      }

      // Validate account number
      if (!formData.ach.accountNumber) {
        newErrors.accountNumber = 'Account number is required';
        isValid = false;
      } else if (!/^\d+$/.test(formData.ach.accountNumber)) {
        newErrors.accountNumber = 'Account number must contain only digits';
        isValid = false;
      }

      // Validate account number confirmation
      if (!accountNumberConfirm) {
        newErrors.accountNumberConfirm = 'Please confirm the account number';
        isValid = false;
      } else if (formData.ach.accountNumber !== accountNumberConfirm) {
        newErrors.accountNumberConfirm = 'Account numbers do not match';
        isValid = false;
      }

      // Validate voided check upload
      if (!formData.ach.voidedCheckFileId) {
        newErrors.voidedCheckFile = 'Please upload a copy of a voided check';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  }, [
    formData,
    accountNumberConfirm
  ]);

  // Add a function to mark all fields as touched
  const markAllFieldsAsTouched = () => {
    const fieldsToTouch: Record<string, boolean> = {
      optIn: true,
    };

    if (formData?.ach?.optIn === true) {
      fieldsToTouch.acknowledgedTerms = true;
      fieldsToTouch.entityName = true;
      fieldsToTouch.bankName = true;
      fieldsToTouch.routingNumber = true;
      fieldsToTouch.accountNumber = true;
      fieldsToTouch.accountNumberConfirm = true;
      fieldsToTouch.voidedCheckFile = true;
    }

    setTouched(fieldsToTouch);
  };

  // Create a validation wrapper that marks all fields as touched when validating
  const validateStepAndMarkTouched = useCallback(() => {
    markAllFieldsAsTouched();
    return validateStep();
  }, [validateStep]);

  // Register validation function with parent component
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validateStepAndMarkTouched);
    }
  }, [onValidationChange, validateStepAndMarkTouched]);

  // Validate on each change to provide real-time feedback
  useEffect(() => {
    validateStep();
  }, [validateStep]);

  const handleOptInChange = (value: boolean) => {
    if (!formData || !setFormData) return;
    
    setTouched(prev => ({
      ...prev,
      optIn: true
    }));
    
    setFormData(prev => ({
      ...prev,
      ach: {
        ...prev.ach,
        optIn: value,
        // Reset acknowledgment if opting out
        acknowledgedTerms: value ? prev.ach?.acknowledgedTerms || false : false
      }
    }));
  };

  const handleAcknowledgmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formData || !setFormData) return;
    
    setTouched(prev => ({
      ...prev,
      acknowledgedTerms: true
    }));
    
    setFormData(prev => ({
      ...prev,
      ach: {
        ...prev.ach,
        acknowledgedTerms: e.target.checked
      }
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formData || !setFormData) return;
    
    const { name, value } = e.target;
    
    // Mark field as touched
    setTouched((prev: Record<string, boolean>) => ({
      ...prev,
      [name]: true
    }));
    
    setFormData((prev: SignupData) => ({
      ...prev,
      ach: {
        ...prev.ach,
        [name]: value,
      },
    }));
  };

  const handleConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formData || !setFormData) return;
    
    // Mark field as touched
    setTouched((prev: Record<string, boolean>) => ({
      ...prev,
      accountNumberConfirm: true
    }));
    
    setAccountNumberConfirm(e.target.value);
    
    // Store in form data as well
    setFormData((prev: SignupData) => ({
      ...prev,
      ach: {
        ...prev.ach,
        accountNumberConfirm: e.target.value
      }
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formData || !setFormData) return;
    
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!formId) {
      setErrors(prev => ({
        ...prev,
        voidedCheckFile: 'Unable to upload file - missing form ID'
      }));
      return;
    }
    
    try {
      // Mark field as touched
      setTouched(prev => ({
        ...prev,
        voidedCheckFile: true
      }));
      
      // Set uploading state
      setUploadingCheck(true);
      
      // Create a temporary upload entry
      setFormData(prev => {
        const currentAch = prev.ach || {};
        
        return {
          ...prev,
          ach: {
            ...currentAch,
            voidedCheckFile: file.name,
            voidedCheckFileName: file.name,
            voidedCheckFileType: file.type,
            voidedCheckFileId: null,
          }
        };
      });
      
      // Upload the file to the server
      const fieldName = 'voidedCheck'; // Use appropriate field name for API
      const response = await formApi.uploadFile(formId, fieldName, file);
      
      // Update the form data with the fileId from the server
      setFormData(prev => {
        const currentAch = prev.ach || {};
        
        return {
          ...prev,
          ach: {
            ...currentAch,
            voidedCheckFile: file.name,
            voidedCheckFileName: file.name,
            voidedCheckFileType: file.type,
            voidedCheckFileId: response.fileId
          }
        };
      });
      
      // Since we now have the file uploaded with a fileId, update the form on the server
      await formApi.updateForm(formId, {
        ach: {
          ...formData.ach,
          voidedCheckFile: file.name,
          voidedCheckFileName: file.name,
          voidedCheckFileType: file.type,
          voidedCheckFileId: response.fileId
        }
      });
    } catch (error) {
      // Handle upload error
      console.error('File upload failed:', error);
      setErrors(prev => ({
        ...prev,
        voidedCheckFile: 'Upload failed. Please try again.'
      }));
    } finally {
      // Clear uploading state
      setUploadingCheck(false);
    }
  };

  // If reinsurance is not enabled, show a message and skip this step
  if (!formData.hasReinsurance) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Payment Information
        </Typography>
        <Alert severity="info">
          Payment information is not required because reinsurance is not enabled.
        </Alert>
      </Box>
    );
  }

  // Safely access form data values
  const ach = formData?.ach || {};

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Payment Information
      </Typography>
      
      <Grid container spacing={3}>
        {/* ACH Opt-in Section */}
        <Grid item xs={12}>
          <FormControl error={touched.optIn && !!errors.optIn} component="fieldset">
            <Box mt={1} mb={2}>
              <Typography variant="body2" color="textSecondary">
                Would you like to authorize ACH payments for reinsurance fees?
              </Typography>
            </Box>
            <RadioGroup
              value={ach.optIn === null || ach.optIn === undefined ? '' : ach.optIn.toString()}
              onChange={(e) => handleOptInChange(e.target.value === 'true')}
            >
              <FormControlLabel 
                value="true" 
                control={<Radio />} 
                label="Yes, I want to opt in to ACH payments" 
              />
              <FormControlLabel 
                value="false" 
                control={<Radio />} 
                label="No, I want to opt out of ACH payments" 
              />
            </RadioGroup>
            {touched.optIn && errors.optIn && (
              <FormHelperText>{errors.optIn}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Show acknowledgment and form fields only if opted in */}
        {ach.optIn === true && (
          <>
            {/* Terms Acknowledgment */}
            <Grid item xs={12}>
              <FormControl error={touched.acknowledgedTerms && !!errors.acknowledgedTerms}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ach.acknowledgedTerms || false}
                      onChange={handleAcknowledgmentChange}
                      name="acknowledgedTerms"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I acknowledge and approve the formation of the reinsurance company is subject to a one-time formation fee of{' '}
                      <strong>
                        {formData.reinsuranceFormationFee
                          ? `$${formData.reinsuranceFormationFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : '$0.00'}
                      </strong>
                      {' '}.  I further acknowledge that an annual fee{' '}
                      {formData.reinsuranceManagementFee > 0 && (
                        <>
                          of{' '}
                          <strong>
                            ${formData.reinsuranceManagementFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </strong>
                          {' '}
                        </>
                      )}
                      will be requested to cover the books, records, licensing and tax preparation. 
                    </Typography>
                  }
                />
                {touched.acknowledgedTerms && errors.acknowledgedTerms && (
                  <FormHelperText>{errors.acknowledgedTerms}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Show ACH form fields only if terms are acknowledged */}
            {ach.acknowledgedTerms && (
              <>
                <Grid item xs={12}>
                  <Box mt={2} mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Please provide the payment details for the entity. The account number must be entered twice for verification.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Entity Name"
                    name="entityName"
                    value={ach.entityName || ""}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={touched.entityName && !!errors.entityName}
                    helperText={touched.entityName && errors.entityName}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Bank Name"
                    name="bankName"
                    value={ach.bankName || ""}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={touched.bankName && !!errors.bankName}
                    helperText={touched.bankName && errors.bankName}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Routing Number"
                    name="routingNumber"
                    value={ach.routingNumber || ""}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={touched.routingNumber && !!errors.routingNumber}
                    helperText={touched.routingNumber && errors.routingNumber}
                  />
                </Grid>

                {/* Account Number and Confirmation on Separate Lines */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Account Number"
                    name="accountNumber"
                    type="password"
                    value={ach.accountNumber || ""}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={touched.accountNumber && !!errors.accountNumber}
                    helperText={touched.accountNumber && errors.accountNumber}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Confirm Account Number"
                    name="accountNumberConfirm"
                    type="password"
                    value={accountNumberConfirm}
                    onChange={handleConfirmChange}
                    fullWidth
                    required
                    error={touched.accountNumberConfirm && !!errors.accountNumberConfirm}
                    helperText={touched.accountNumberConfirm && errors.accountNumberConfirm}
                  />
                </Grid>
                
                {/* Voided Check Upload */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Voided Check Upload
                  </Typography>
                  <Box sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1, mb: 1 }}>
                    <input
                      accept="image/*,.pdf"
                      style={{ display: 'none' }}
                      id="voided-check-upload"
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploadingCheck}
                    />
                    <label htmlFor="voided-check-upload">
                      <Button
                        variant="contained"
                        component="span"
                        color={errors.voidedCheckFile && touched.voidedCheckFile ? "error" : "primary"}
                        disabled={uploadingCheck}
                        startIcon={uploadingCheck ? <CircularProgress size={20} /> : undefined}
                      >
                        {uploadingCheck ? 'Uploading...' : 'Upload Voided Check'}
                      </Button>
                      {ach.voidedCheckFileName && (
                        <Box component="span" sx={{ ml: 2 }}>
                          {ach.voidedCheckFileName}
                          {ach.voidedCheckFileId && ' ✓'}
                        </Box>
                      )}
                    </label>
                  </Box>
                  {touched.voidedCheckFile && errors.voidedCheckFile && (
                    <Typography color="error" variant="caption">
                      {errors.voidedCheckFile}
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary">
                    Please upload a voided check or bank document showing the account and routing numbers.
                  </Typography>
                </Grid>
              </>
            )}
          </>
        )}

        {/* Show success message if opted out */}
        {ach.optIn === false && (
          <Grid item xs={12}>
            <Alert severity="success">
              You have opted out of ACH payments. You may proceed to the next step.
            </Alert>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default PaymentStep;